/**
 * The Knytt Stories main-menu cloud (yknytt MenuCloud + ParticleCircle): four rotating
 * rings of soft dots (inner yellow / red / outer yellow / gray) plus a scattered red
 * cloud, on a white background. Faithful to MenuCloud.tscn's ring parameters.
 */
interface CircleRing {
  cloud: false;
  radius: number;
  count: number;
  startAngle: number;
  da: number; // angle between particles
  speed: number; // ring rotation, rad/s
  color: string;
  scale: number;
  angle: number; // current ring rotation
}
interface CloudRing {
  cloud: true;
  color: string;
  scale: number;
  // per-particle arms (own radius/angle/speed)
  arms: { radius: number; angle: number; speed: number }[];
}
type Ring = CircleRing | CloudRing;

const YELLOW = '#b2ae00';
const RED = '#ff3d33';
const GRAY = '#808080';
const DOT_PX = 32; // source sprite size; visible dot ≈ DOT_PX * scale * UNIT

export class MenuCloud {
  readonly canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private rings: Ring[];
  private raf = 0;
  private last = 0;
  private running = false;

  /** compact: fill a small parent (pause panel) — centred, sized to the parent box. */
  constructor(
    parent: HTMLElement,
    private compact = false,
  ) {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'menu-cloud';
    parent.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d')!;

    // Deterministic RNG (yknytt seeds MenuCloud) so the scatter is stable.
    let s = 1337;
    const rnd = (): number => {
      s = (s * 1103515245 + 12345) & 0x7fffffff;
      return s / 0x7fffffff;
    };
    const range = (a: number, b: number): number => a + rnd() * (b - a);

    // Four rotating rings (ParticleCircle circle mode), from MenuCloud.tscn.
    this.rings = [
      { cloud: false, radius: 25, count: 9, startAngle: 0, da: 0.698, speed: 0.28, color: YELLOW, scale: 0.16, angle: 0 },
      { cloud: false, radius: 35, count: 8, startAngle: -0.7, da: 0.258, speed: -0.28, color: RED, scale: 0.16, angle: 0 },
      { cloud: false, radius: 45, count: 15, startAngle: 0, da: 0.419, speed: -0.35, color: YELLOW, scale: 0.16, angle: 0 },
      { cloud: false, radius: 55, count: 15, startAngle: 0, da: 0.209, speed: 0.25, color: GRAY, scale: 0.16, angle: 0 },
    ];
    // Scattered red cloud (cloud mode): 90 arms, radius 65-85, random angles + speeds.
    const arms: CloudRing['arms'] = [];
    let angle = 0;
    for (let i = 0; i < 90; i++) {
      arms.push({ radius: range(65, 85), angle, speed: range(-0.1, -0.4) });
      angle += range(0, 6.28);
    }
    this.rings.push({ cloud: true, color: RED, scale: 0.094, arms });

    this.resize();
    window.addEventListener('resize', this.resize);
  }

  start(): void {
    if (this.running) return;
    this.resize(); // the parent may only just have been attached/laid out
    this.running = true;
    this.last = performance.now();
    this.raf = requestAnimationFrame(this.frame);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }

  /** CSS-pixel size of the drawing surface (parent box; the menu's parent is fullscreen). */
  private box(): { w: number; h: number } {
    const p = this.canvas.parentElement;
    return p && this.compact
      ? { w: p.clientWidth, h: p.clientHeight }
      : { w: window.innerWidth, h: window.innerHeight };
  }

  private resize = (): void => {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const { w, h } = this.box();
    this.canvas.width = w * dpr;
    this.canvas.height = h * dpr;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  private frame = (now: number): void => {
    if (!this.running) return;
    const dt = Math.min((now - this.last) / 1000, 0.1);
    this.last = now;
    for (const r of this.rings) {
      if (r.cloud) for (const arm of r.arms) arm.angle += arm.speed * dt;
      else r.angle += r.speed * dt;
    }
    this.draw();
    this.raf = requestAnimationFrame(this.frame);
  };

  private draw(): void {
    const ctx = this.ctx;
    const { w, h } = this.box();
    ctx.clearRect(0, 0, w, h);
    // Fullscreen menu: centred horizontally, upper-middle (above the button row).
    // Compact (pause panel): dead centre, scaled to the panel box (yknytt's 0.55 cloud).
    const cx = w / 2;
    const cy = this.compact ? h / 2 : h * 0.4;
    // Scale the compact (radius ≤ 85) cloud up to a pleasant on-screen size.
    const UNIT = this.compact ? h / 190 : Math.max(2.2, Math.min(3.6, Math.min(w, h) / 230));

    for (const r of this.rings) {
      if (r.cloud) {
        for (const arm of r.arms) {
          const x = cx + Math.cos(arm.angle) * arm.radius * UNIT;
          const y = cy + Math.sin(arm.angle) * arm.radius * UNIT;
          this.dot(x, y, (DOT_PX * r.scale * UNIT) / 2, r.color);
        }
      } else {
        for (let i = 0; i < r.count; i++) {
          const a = r.angle + r.startAngle + r.da * i;
          const x = cx + Math.cos(a) * r.radius * UNIT;
          const y = cy + Math.sin(a) * r.radius * UNIT;
          this.dot(x, y, (DOT_PX * r.scale * UNIT) / 2, r.color);
        }
      }
    }
  }

  /** A soft dot (radial gradient: opaque centre → transparent edge), like the svgs. */
  private dot(x: number, y: number, radius: number, color: string): void {
    const ctx = this.ctx;
    const grad = ctx.createRadialGradient(x, y, 0, x, y, radius);
    grad.addColorStop(0, color);
    grad.addColorStop(0.55, this.withAlpha(color, 0.9));
    grad.addColorStop(1, this.withAlpha(color, 0));
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
  }

  private withAlpha(hex: string, a: number): string {
    const n = parseInt(hex.slice(1), 16);
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
  }

  destroy(): void {
    this.stop();
    window.removeEventListener('resize', this.resize);
    this.canvas.remove();
  }
}
